import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCallback, useState, useEffect } from 'react';
import { useDonationContextProvider } from 'context/DonationContextProvider';
import { useBranchContext } from 'context/BranchContext';

import { DONATION_STEPS } from '../data';

import { DonationFooter } from '../DonationFooter';
import { DonationHeader } from '../DonationHeader';
import { GiftAidCard } from '../GiftAidCard';
import { DonationSummary } from './donation-summary';
import { DonorForm, donorFormDefaultValues, donorSchema } from './donor-form';
import { createDonationPaymentIntent, persistBasketSession } from '../../../lib/supabaseQueries';

function DonorInformationPage() {
  const { branchId } = useBranchContext(); // Get branch from subdomain
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    setDonationStep,
    setDonor,
    donor,
    basket,
    giftAid,
    giftAidData,
    transactionFeeAmount,
    subtotal,
    totalAmount,
    transactionFee,
    setClientSecret,
    setStripeKey,
    donationStep
  } = useDonationContextProvider();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: donorFormDefaultValues,
    resolver: yupResolver(donorSchema)
  });

  // Populate form with saved donor data when navigating back, or reset to defaults
  useEffect(() => {
    if (donationStep === DONATION_STEPS.DONOR_INFORMATION) {
      if (donor && Object.keys(donor).length > 0) {
        form.reset({ ...donorFormDefaultValues, ...donor });
      } else {
        form.reset(donorFormDefaultValues);
      }
    }
  }, [donationStep, form, donor]);

  const buildDonorObject = useCallback((donorData) => {
    const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

    return {
      // Required fields
      first_name: donorData.firstName,
      last_name: donorData.lastName,
      branch_id: branchId,
      organisation_id: organizationId,

      // How did you find us (optional)
      hear_about_us: donorData.hearAboutUs || null,

      // Email - array format
      email: [
        {
          email: donorData.email,
          isPrimary: true
        }
      ],
      primary_email: donorData.email,

      // Phone - array format
      phone: donorData.phoneNumber
        ? [
          {
            phoneNumber: donorData.phoneNumber,
            isPrimary: true
          }
        ]
        : [],
      primary_phone: donorData.phoneNumber || null,

      // Address - array format
      address: [
        {
          addressLine1: donorData.addressLine1 || '',
          addressLine2: donorData.addressLine2 || '',
          city: donorData.city || '',
          country: donorData.country || 'GB',
          postcode: donorData.postcode || '',
          isPrimary: true
        }
      ],
      primary_address: donorData.addressLine1 || null,

      // Defaults
      type: 'individual',
      title: donorData.title || 'Mr'
    };
  }, [branchId]);

  const buildOrderDetails = useCallback((basket) => {
    return basket.map(item => ({
      amount: item.count * item.donationAmount.value,
      billing_condition: item.paymentType,
      donation_type_id: item.donationType.id,
      package_id: item.packageId || null
    }));
  }, []);

  const initializePayment = useCallback(async (donorObject, orderDetails) => {
    const isRecurring = basket.some(item => item.paymentType === 'monthly');

    const { data, error } = await createDonationPaymentIntent({
      donorObject,
      amount: parseFloat(totalAmount),
      orderDetails,
      subtotal: parseFloat(subtotal),
      transactionFees: transactionFee ? parseFloat(transactionFeeAmount) : 0,
      giftAidEligible: giftAid,
      giftAidData: giftAid ? giftAidData : null,
      isRecurring
    });

    if (error) {
      throw new Error(error);
    }

    if (!data?.client_secret) {
      throw new Error('Failed to create payment intent');
    }

    setClientSecret(data.client_secret);
    if (data.publishable_key) {
      setStripeKey(data.publishable_key);
    }
    return data
  }, [totalAmount, subtotal, transactionFee, transactionFeeAmount, giftAid, giftAidData, basket, setClientSecret, setStripeKey]);

  const handleCheckout = useCallback(async (data) => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      setDonor(data);

      const donorObject = buildDonorObject(data);
      const orderDetails = buildOrderDetails(basket);
      const organizationId = process.env.REACT_APP_ORGANIZATION_ID;

      const response = await initializePayment(donorObject, orderDetails);

      console.log('Payment initialized, donor_id:', response.donor_id);

      // Persist basket session with all data for recovery
      if (response?.donor_id) {
        const sessionResult = await persistBasketSession({
          donorId: response.donor_id,
          basket,
          transactionFee: transactionFee ? transactionFeeAmount : 0,
          subtotal,
          totalAmount,
          branchId,
          organisationId: organizationId,
          giftAidEligible: giftAid,
          giftAidData: giftAid ? giftAidData : null,
          orderDetails,
          paymentIntentId: response.payment_intent_id
        });

        if (sessionResult.error) {
          console.error('Failed to create basket session:', sessionResult.error);
        } else {
          console.log('Basket session created:', sessionResult.data?.id);
        }
      }

      setDonationStep(DONATION_STEPS.PAYMENT);
    } catch (error) {
      toast.error(error.message || 'Something went wrong');
      console.error('Payment initialization error:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, setDonor, buildDonorObject, buildOrderDetails, basket, initializePayment, setDonationStep, transactionFee, transactionFeeAmount, subtotal, totalAmount, branchId, giftAid, giftAidData]);

  return (
    <div className="bg-wr-donation-grey-50">
      <DonationHeader />
      <div className="container py-6 ">
        <div className="row g-4">
          <div
            className="col-12 col-md-8 d-flex flex-column gap-4"
          // style={{ minHeight: `calc(100vh - ${HEADER_HEIGHT}px)` }}
          >
            <DonorForm form={form} />
            {/* <DonorForm form={form} /> */}
            <div className="">
              <GiftAidCard logoPosition="right" />
            </div>
          </div>
          <div
            className="col-12 col-md-4 "
          // style={{ minHeight: `calc(100vh - ${HEADER_HEIGHT}px)` }}
          >
            <DonationSummary
              onCheckout={form.handleSubmit(handleCheckout)}
              onClickBack={() =>
                setDonationStep(DONATION_STEPS.AUTH)
              }
              isProcessing={isProcessing}
            />
          </div>
        </div>
      </div>
      <DonationFooter />
    </div>
  );
}

export { DonorInformationPage };
